import React, { Suspense } from 'react';
import { Box, Button, Typography, Skeleton } from '@mui/material';
import { ContentCopy, Check, Directions, DirectionsCar, Place, DirectionsRun, FlagCircle } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { RootState } from '@/store';
import type { PlayerDepartureEntry } from '@/components/DepartureTimeDisplay';
import type { MapMarker } from '@/components/mapTypes';
import { formatInUserTimezone } from '@/utils/timezoneUtils';

const InlineMapPreview = React.lazy(() => import('@/components/InlineMapPreview'));

interface VenuePopupContentProps {
  venue: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  departureEntries?: PlayerDepartureEntry[];
  markers?: MapMarker[];
  distanceUnit?: 'MILES' | 'KILOMETERS';
  matchTime?: string | null;
  onCopyAddress?: () => void;
  onDirections?: () => void;
  copied?: boolean;
}

/**
 * Content for the venue popup/drawer. Shows:
 * - Summary cards (distance, drive time, leave by)
 * - Embedded map
 * - Match day timeline
 */
const VenuePopupContent: React.FC<VenuePopupContentProps> = ({
  venue,
  address,
  latitude,
  longitude,
  departureEntries,
  markers,
  distanceUnit = 'MILES',
  matchTime,
  onCopyAddress,
  onDirections,
  copied,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const orgSettings = useSelector((state: RootState) => state.organization.settings);

  // Get the first valid departure entry for summary cards
  const primaryEntry = departureEntries?.find(
    e => !e.departureInfo.noTravelRequired && e.departureInfo.departureTime !== null
  );

  const formatTime = (date: Date | string) => {
    const iso = typeof date === 'string' ? date : dayjs(date).toISOString();
    return formatInUserTimezone(iso, 'h:mm A', false, user, orgSettings);
  };

  const formatDistance = (meters?: number) => {
    if (!meters) return null;
    if (distanceUnit === 'KILOMETERS') {
      const km = meters / 1000;
      return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
    }
    const miles = meters / 1609.34;
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
  };

  const formatTravelTime = (minutes: number) => {
    if (minutes >= 60) {
      const h = Math.floor(minutes / 60);
      const m = Math.round(minutes % 60);
      return m > 0 ? `~${h}h ${m}m` : `~${h}h`;
    }
    return `~${Math.round(minutes)} min`;
  };

  const distanceStr = formatDistance(primaryEntry?.departureInfo.distanceMeters);
  const driveStr = primaryEntry ? formatTravelTime(primaryEntry.departureInfo.baseTravelTimeMinutes || primaryEntry.departureInfo.travelTimeMinutes) : null;
  const leaveByStr = primaryEntry?.departureInfo.departureTime
    ? formatTime(new Date(primaryEntry.departureInfo.departureTime))
    : null;

  // Build timeline entries
  const timelineEntries: { icon: React.ReactNode; label: string; time: string; accented: boolean }[] = [];
  if (primaryEntry?.departureInfo.departureTime) {
    timelineEntries.push({
      icon: <DirectionsCar sx={{ fontSize: 16 }} />,
      label: 'Leave home',
      time: formatTime(new Date(primaryEntry.departureInfo.departureTime)),
      accented: true,
    });
  }
  // Arrive = departure + travel time
  if (primaryEntry?.departureInfo.departureTime && primaryEntry.departureInfo.travelTimeMinutes > 0) {
    const arriveDate = dayjs(primaryEntry.departureInfo.departureTime)
      .add(primaryEntry.departureInfo.baseTravelTimeMinutes || primaryEntry.departureInfo.travelTimeMinutes, 'minute');
    timelineEntries.push({
      icon: <Place sx={{ fontSize: 16 }} />,
      label: `Arrive at ${venue}`,
      time: formatTime(arriveDate.toDate()),
      accented: false,
    });
  }
  // Warmup = match - warmup minutes
  if (matchTime && primaryEntry?.departureInfo.warmupMinutes) {
    const warmupDate = dayjs(matchTime).subtract(primaryEntry.departureInfo.warmupMinutes, 'minute');
    timelineEntries.push({
      icon: <DirectionsRun sx={{ fontSize: 16 }} />,
      label: 'Warmup starts',
      time: formatTime(warmupDate.toDate()),
      accented: false,
    });
  }
  // Kickoff
  if (matchTime) {
    const tz = formatInUserTimezone(matchTime, 'z', false, user, orgSettings);
    timelineEntries.push({
      icon: <FlagCircle sx={{ fontSize: 16 }} />,
      label: 'Kickoff',
      time: `${formatTime(matchTime)} ${tz}`,
      accented: true,
    });
  }

  const hasCoords = !!(latitude && longitude);

  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      {/* Title */}
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary', mb: 0.5, pr: 4 }}>Venue & Directions</Typography>
      {/* Venue name */}
      <Typography sx={{ fontWeight: 500, fontSize: '13px', color: 'text.secondary', mb: 0.25 }}>{venue}</Typography>
      {address && (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>{address}</Typography>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {address && onCopyAddress && (
          <Button
            variant="outlined"
            size="small"
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 'bold', fontSize: '0.8rem' }}
            startIcon={
              <Box sx={{ position: 'relative', width: 16, height: 16 }}>
                <ContentCopy sx={{ fontSize: 16, position: 'absolute', inset: 0, transition: 'opacity 0.2s, transform 0.2s', opacity: copied ? 0 : 1, transform: copied ? 'scale(0.5)' : 'scale(1)' }} />
                <Check sx={{ fontSize: 16, position: 'absolute', inset: 0, transition: 'opacity 0.2s, transform 0.2s', opacity: copied ? 1 : 0, transform: copied ? 'scale(1)' : 'scale(0.5)' }} />
              </Box>
            }
            onClick={onCopyAddress}
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        )}
        {hasCoords && onDirections && (
          <Button
            variant="contained"
            size="small"
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 'bold', fontSize: '0.8rem' }}
            startIcon={<Directions sx={{ fontSize: 16 }} />}
            onClick={onDirections}
          >
            Directions
          </Button>
        )}
      </Box>

      {/* Summary cards */}
      {(distanceStr || driveStr || leaveByStr) && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {leaveByStr && (
            <SummaryCard
              label="LEAVE BY"
              value={leaveByStr}
              variant="accent"
            />
          )}
          {distanceStr && (
            <SummaryCard label="DISTANCE" value={distanceStr} />
          )}
          {driveStr && (
            <SummaryCard label="DRIVE" value={driveStr} />
          )}
        </Box>
      )}

      {/* Map */}
      {hasCoords && (
        <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <Suspense fallback={<Skeleton variant="rectangular" height={180} />}>
            <InlineMapPreview
              latitude={latitude!}
              longitude={longitude!}
              height={180}
              markers={markers}
              distanceUnit={distanceUnit}
              interactive
            />
          </Suspense>
        </Box>
      )}

      {/* Match day timeline */}
      {timelineEntries.length > 1 && (
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', letterSpacing: '0.08em', mb: 1 }}>
            MATCH DAY TIMELINE
          </Typography>
          {timelineEntries.map((entry, i) => (
            <Box key={i}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: entry.accented ? 'text.primary' : 'text.secondary', display: 'flex' }}>
                  {entry.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '0.8rem',
                    fontWeight: entry.accented ? 600 : 400,
                    color: entry.accented ? 'text.primary' : 'text.secondary',
                  }}>
                    {entry.label}
                  </Typography>
                </Box>
                <Typography sx={{
                  fontSize: '0.8rem',
                  fontWeight: entry.accented ? 600 : 400,
                  color: entry.accented ? 'text.primary' : 'text.secondary',
                  flexShrink: 0,
                }}>
                  {entry.time}
                </Typography>
              </Box>
              {/* Connecting line */}
              {i < timelineEntries.length - 1 && (
                <Box sx={{ ml: '7px', width: 2, minHeight: 12, bgcolor: 'divider', my: 0.25 }} />
              )}
            </Box>
          ))}
        </Box>
      )}

    </Box>
  );
};

const SummaryCard: React.FC<{ label: string; value: string; variant?: 'accent' | 'default' }> = ({ label, value, variant = 'default' }) => (
  <Box sx={{
    flex: 1,
    textAlign: 'center',
    py: 1.5,
    px: 1,
    borderRadius: 2,
    border: '1px solid',
    ...(variant === 'accent'
      ? { bgcolor: '#fef3c7', borderColor: '#fde68a' }
      : { bgcolor: 'action.hover', borderColor: 'divider' }
    ),
  }}>
    <Typography sx={{
      fontSize: '0.5rem',
      fontWeight: 700,
      color: variant === 'accent' ? '#92400e' : 'text.secondary',
      letterSpacing: '0.1em',
      mb: 0.5,
    }}>
      {label}
    </Typography>
    <Typography sx={{
      fontSize: '1.1rem',
      fontWeight: 800,
      color: variant === 'accent' ? '#92400e' : 'text.primary',
      lineHeight: 1,
    }}>
      {value}
    </Typography>
  </Box>
);

VenuePopupContent.displayName = 'VenuePopupContent';

export default VenuePopupContent;
